import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { FormModal } from '../../../modal/form-modal';
import { UnitSelect } from '../../../capture/unit-select';
import { getUnit } from '../../../../api/hierarchy-client';
import { UnitDetail } from '../../../../interfaces/hierarchy-interfaces';
import { StateSwitch } from '../../../../motion';
import {
  OccupantBlock,
  OccupantPasses,
  useUnitPasses,
} from '../../dashboard/dashboard-units-components/unit-passes';
import { occupantFullName } from '../../dashboard/dashboard-units-components/units-shared';
import {
  ListSkeleton,
  LoadError,
} from '../../dashboard/dashboard-shared-components/load-state';
import { palette, spacing, typography } from '../../../../style-utils/theme';

/**
 * THE FRONT DESK'S OWN PASS WRITES (migration 118).
 *
 * Opened from "Add expected visitor" on the Expected screen, and only offered
 * when the building lets this desk add passes (pass_access.can_add). The desk
 * picks the unit; each current resident of it then gets the SAME block the unit
 * screen shows a manager (OccupantPasses): their live passes, "Add expected
 * visitor for <name>", and whichever Edit / Pause / End the server says this
 * desk user may do (can_edit / can_end). Nothing here decides a permission —
 * at a "one-time visits only" building the list answers once_only and the
 * editor opens in staff-once mode; everything is re-checked by the server.
 *
 * Every change tells the household (RP-2.13), which the subtitle says, so the
 * desk knows a pass nobody asked for will be noticed.
 */

type UnitState = 'idle' | 'loading' | 'error' | 'ready';

export interface DeskPassesModalProps {
  open: boolean;
  onClose: () => void;
  buildingId: number;
  /** The building allows the desk one-time visits only. */
  onceOnly: boolean;
}

export const DeskPassesModal: React.FC<DeskPassesModalProps> = ({
  open,
  onClose,
  buildingId,
  onceOnly,
}) => {
  const [unitLabel, setUnitLabel] = useState('');
  const [unitId, setUnitId] = useState<number | null>(null);
  const [unit, setUnit] = useState<UnitDetail | null>(null);
  const [state, setState] = useState<UnitState>('idle');
  const [attempt, setAttempt] = useState(0);

  // Each opening starts from an empty picker: yesterday's unit is not today's.
  useEffect(() => {
    if (!open) {
      setUnitLabel('');
      setUnitId(null);
    }
  }, [open]);

  useEffect(() => {
    if (!unitId) {
      setUnit(null);
      setState('idle');

      return undefined;
    }
    let live = true;
    setState('loading');
    getUnit(unitId)
      .then(detail => {
        if (!live) return;
        setUnit(detail);
        setState('ready');
      })
      .catch(() => {
        if (live) setState('error');
      });

    return () => {
      live = false;
    };
  }, [unitId, attempt]);

  const occupants = unit?.occupants ?? [];
  const passes = useUnitPasses(buildingId, unit?.id, state === 'ready' && occupants.length > 0);

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title="Expected visitors"
      subtitle={
        onceOnly
          ? 'One-time visits for today or tomorrow. The household is told about every change.'
          : 'The household is told about every change.'
      }
    >
      <Column>
        <UnitSelect
          label="Unit"
          value={unitLabel}
          onChange={(label, id) => {
            setUnitLabel(label);
            setUnitId(id);
          }}
          buildingId={buildingId}
          unitId={unitId}
          placeholder="Type the unit number"
        />

        <StateSwitch state={state}>
          {state === 'idle' ? (
            <Note>Choose the unit the visitor is coming to.</Note>
          ) : state === 'loading' ? (
            <ListSkeleton rows={1} rowHeight={56} label="the unit's residents" />
          ) : state === 'error' ? (
            <LoadError
              message="Could not load this unit's residents."
              onRetry={() => setAttempt(n => n + 1)}
            />
          ) : occupants.length === 0 ? (
            <Note>
              {`Nobody lives in ${unit?.unit_number ?? 'this unit'} now, so there is no one to add a visitor for.`}
            </Note>
          ) : (
            <Residents>
              {occupants.map(occupant => (
                <OccupantBlock key={occupant.id}>
                  <ResidentName>{occupantFullName(occupant)}</ResidentName>
                  <OccupantPasses
                    occupant={occupant}
                    unitLabel={unit?.unit_number ?? unitLabel}
                    buildingId={buildingId}
                    passes={passes}
                    canManage
                  />
                </OccupantBlock>
              ))}
            </Residents>
          )}
        </StateSwitch>
      </Column>
    </FormModal>
  );
};

/* ──────────────────────────────  Styles  ──────────────────────────────── */

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const Residents = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const ResidentName = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Note = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;
